import { Component, h, Host, Prop, Listen, AttachInternals, Watch } from '@stencil/core';
import { v4 as uuidv4 } from 'uuid';
import { IconName } from '../icon/icons';
import { AnchorRel, AnchorTarget } from '../../types';

export type ButtonType = 'button' | 'submit' | 'reset';

export type IconPosition = 'none' | 'right' | 'left';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary';

export type GapSize = 'small' | 'big';

/**
 * @slot start-content - Slot for the start content of the button
 * @slot end-content - Slot for the end content of the button
 */
@Component({
  tag: 'anchor-button',
  styleUrl: 'button.scss',
  shadow: true,
  formAssociated: true,
})
export class Button {
  @AttachInternals() internals: ElementInternals;

  /**
   *  Give this instance of button a custom ID, or keep the default.
   */
  @Prop({}) uuid: string = uuidv4();

  /**
   *  Set a text to be displayed in the content slot of the button
   */
  @Prop() text: string;

  /**
   *  Set the variant of the button
   */
  @Prop() variant?: ButtonVariant = 'primary';

  /**
   * Set to true if the Button is displayed on dark background
   */
  @Prop({ mutable: true }) onDark?: boolean = false;

  /**
   *  Set an icon to be displayed on the button
   */
  @Prop() icon?: IconName;

  /**
   *  Set the type of button
   */
  @Prop() type?: ButtonType = 'button';

  /**
   *  Set the position of the icon on a button with an icon
   */
  @Prop() iconPlacement?: IconPosition = 'none';

  /**
   *  Set whether or not the button should be disabled
   *
   * **NB!** This should be rarely be used as it's a potential accessibility issue. Disabled buttons are ignored by screen readers and keyboard navigation.
   */
  @Prop({ mutable: true, reflect: true }) disabled?: boolean = false;

  /**
   * Set the Button state to loading to display a spinner icon
   */
  @Prop({ mutable: true, reflect: true }) loading: boolean = false;

  /**
   * Contains a URL or a URL fragment that the hyperlink points to.
   * If this property is set, an anchor tag will be rendered.
   * [Documentation](https://developer.mozilla.org/en-US/docs/Web/API/HTMLAnchorElement/host)
   */
  @Prop() href: string | undefined;

  /**
   * Specifies the relationship of the target object to the link object.
   * The value is a space-separated list of [link types](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/rel).
   */
  @Prop() rel: AnchorRel | undefined;

  /**
   * Specifies where to display the linked URL.
   * Only applies when an `href` is provided.
   * Special keywords: `"_blank"`, `"_self"`, `"_parent"`, `"_top"`.
   * [Documentation](https://developer.mozilla.org/en-US/docs/Web/API/HTMLAnchorElement/target)
   */
  @Prop() target: AnchorTarget | undefined;

  /**
   * This attribute instructs browsers to download a URL instead of navigating to
   * it, so the user will be prompted to save it as a local file. If the attribute
   * has a value, it is used as the pre-filled file name in the Save prompt
   * (the user can still change the file name if they want).
   * [Documentation](https://developer.mozilla.org/en-US/docs/Web/API/HTMLAnchorElement/download)
   */
  @Prop() download: string | undefined;

  /**
   * The ping property is a space-separated list of URLs.
   * When the link is followed, the browser will send POST requests with the body PING to the URLs.
   * 
   * [Documentation](https://developer.mozilla.org/en-US/docs/Web/API/HTMLAnchorElement/ping)
   */
  @Prop() ping: string | undefined;

  /**
   * START Special handling of disabled buttons
   * 
   * After making button formAssociated the disabled attribute works as defined for HTML Form elements:
   * - If disabled attribute exists, no matter the value of it, the element is disabled.
   * 
   * That is a breaking change, as many applications expects that they can just set disabled="false" to enable it.
   * 
   * The following logic ensures that when disabled is set to false, then the attribute is simply removed from the element.
   */
  @Watch('disabled')
  handleDisabled() {
    if(this.disabled == false) {
      this.disabled = undefined;
    }
  }

  componentDidLoad() {
    this.handleDisabled();
  }

  /**
   * END Special handling of disabled buttons
   */

  @Listen('click')
  handleClick() {
    if (this.type === 'submit') {
      this.internals.form.requestSubmit();
    }
  }

  render() {
    const DetectedTag = this.href === undefined ? 'button' : ('a' as any);
    const attrs =
      DetectedTag === 'button'
        ? { type: this.type }
        : {
            download: this.download,
            href: this.href,
            rel: this.rel,
            target: this.target,
            ping: this.ping
          };

    const buttonClass = `
      anchor-c-btn 
      anchor-c-btn--${this.variant}${this.onDark ? '--on-dark' : ''}
      ${this.iconPlacement === 'left' ? 'anchor-c-btn--icon-left' : ''} 
      ${this.loading ? 'loading' : ''}
      `;
    return (
      <Host aria-disabled={this.disabled ? 'true' : null} style={{ pointerEvents: this.disabled && this.disabled === true ? 'none' : undefined }}>
        <DetectedTag {...attrs} id={this.uuid} class={buttonClass} disabled={this.disabled ? true: null}>
          {this.loading && <anchor-spinner class="anchor-c-btn__spinner"></anchor-spinner>}
          <slot name="start-content" />
          <span class="anchor-c-btn__text">{this.text}</span>
          <slot name="end-content" />
          {this.icon && <anchor-icon name={this.icon} size={24} stroke={1.5} class="anchor-c-btn__icon"></anchor-icon>}
        </DetectedTag>
      </Host>
    );
  }
}
